import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { lazyPage } from './lib/lazyPage';
import ScrollToTop from './components/ScrollToTop';
import PullToRefresh from './components/PullToRefresh';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { APP_REFRESH_EVENT } from './hooks/useAppRefresh';
import { App as CapApp } from '@capacitor/app';
import { checkForUpdate } from './services/liveUpdate';
import { AuthProvider, useAuth } from './context/AuthContext';
import { usePushNotifications } from './hooks/usePushNotifications';
import { useTimesheetReminder } from './hooks/useTimesheetReminder';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LicenseGate from './components/auth/LicenseGate';
import WebOnlyRoute from './components/auth/WebOnlyRoute';
import MovedRoute from './components/auth/MovedRoute';
import AccountsOrNurseryGate from './components/accounts/AccountsOrNurseryGate';
import { retailAlso } from './hooks/useStockSeat';
import { retailSells } from './hooks/useSellSeat';

// Who reads an order: the nursery's operators and up (not pickers), whoever
// sells (Sales, Retail), and Accounts. Entering one is Sales' (useSellSeat).
const ORDER_READERS = [
  { moduleKey: 'retail', licenseType: ['staff', 'manager'] },
  { moduleKey: 'sales' },
  { moduleKey: 'accounts' },
];
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import Landing from './pages/Landing';
const FeaturePage = lazyPage(() => import('./pages/public/FeaturePage'));
const Pricing = lazyPage(() => import('./pages/public/Pricing'));
const PrivacyPolicy = lazyPage(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = lazyPage(() => import('./pages/TermsAndConditions'));
import PlatformTermsGate from './components/legal/PlatformTermsGate';
import OrgChangedNotice from './components/ui/OrgChangedNotice';
import UpdateAvailableBanner from './components/ui/UpdateAvailableBanner';
import ModuleTermsGate from './components/legal/ModuleTermsGate';
const Dashboard = lazyPage(() => import('./pages/Dashboard'));
const TimesheetHome = lazyPage(() => import('./pages/TimesheetHome'));
const TimesheetHistoryPage = lazyPage(() => import('./pages/TimesheetHistory'));
const MyTimesheet = lazyPage(() => import('./pages/MyTimesheet'));
const ResumeWork = lazyPage(() => import('./pages/ResumeWork'));
const AdminManagement = lazyPage(() => import('./pages/AdminManagement'));
const JoinOrganisation = lazyPage(() => import('./pages/JoinOrganisation'));
const SuperAdminDashboard = lazyPage(() => import('./pages/SuperAdminDashboard'));
const HRManagement = lazyPage(() => import('./pages/HRManagement'));
const StaffProfile = lazyPage(() => import('./pages/StaffProfile'));
const AwardReviews = lazyPage(() => import('./pages/hr/AwardReviews'));
const HRLiveDashboard = lazyPage(() => import('./pages/HRLiveDashboard'));
const PayCycleDashboard = lazyPage(() => import('./pages/PayCycleDashboard'));
const PayPeriodShadow = lazyPage(() => import('./pages/hr/PayPeriodShadow'));
const CompanyPolicies = lazyPage(() => import('./pages/CompanyPolicies'));
const WhsDashboard = lazyPage(() => import('./pages/hr/whs/WhsDashboard'));
const WhsReport = lazyPage(() => import('./pages/hr/whs/WhsReport'));
const WhsTrackReport = lazyPage(() => import('./pages/hr/whs/WhsTrackReport'));
const WhsReports = lazyPage(() => import('./pages/hr/whs/WhsReports'));
const WhsReportDetail = lazyPage(() => import('./pages/hr/whs/WhsReportDetail'));
const WhsRiskAssessments = lazyPage(() => import('./pages/hr/whs/WhsRiskAssessments'));
const WhsRiskAssessmentDetail = lazyPage(() => import('./pages/hr/whs/WhsRiskAssessmentDetail'));
const WhsActions = lazyPage(() => import('./pages/hr/whs/WhsActions'));
const WhsInspections = lazyPage(() => import('./pages/hr/whs/WhsInspections'));
const WhsInspectionDetail = lazyPage(() => import('./pages/hr/whs/WhsInspectionDetail'));
const WhsAudits = lazyPage(() => import('./pages/hr/whs/WhsAudits'));
const WhsAuditDetail = lazyPage(() => import('./pages/hr/whs/WhsAuditDetail'));
const WhsDocuments = lazyPage(() => import('./pages/hr/whs/WhsDocuments'));
const WhsTraining = lazyPage(() => import('./pages/hr/whs/WhsTraining'));
const WhsEmergencyPlans = lazyPage(() => import('./pages/hr/whs/WhsEmergencyPlans'));
const WhsObjectives = lazyPage(() => import('./pages/hr/whs/WhsObjectives'));
const WhsManagementReviews = lazyPage(() => import('./pages/hr/whs/WhsManagementReviews'));
const WhsSettings = lazyPage(() => import('./pages/hr/whs/WhsSettings'));
const MyCertifications = lazyPage(() => import('./pages/certifications/MyCertifications'));
const CertificationsAdmin = lazyPage(() => import('./pages/certifications/CertificationsAdmin'));
import InductionGate from './components/induction/InductionGate';
import LanguageSync from './i18n/LanguageSync';
const InductionsHub = lazyPage(() => import('./pages/inductions/InductionsHub'));
const Compliance = lazyPage(() => import('./pages/compliance/Compliance'));
const EngagementDetail = lazyPage(() => import('./pages/compliance/EngagementDetail'));
const AuditorPortal = lazyPage(() => import('./pages/audit/AuditorPortal'));
const CustomerInvite = lazyPage(() => import('./pages/CustomerInvite'));
const InductionDetail = lazyPage(() => import('./pages/inductions/InductionDetail'));
const InductionPerson = lazyPage(() => import('./pages/inductions/InductionPerson'));
const MyInductions = lazyPage(() => import('./pages/inductions/MyInductions'));
const MyWarnings = lazyPage(() => import('./pages/MyWarnings'));
const ReadPolicy = lazyPage(() => import('./pages/inductions/ReadPolicy'));
const ReadEquipmentInduction = lazyPage(() => import('./pages/inductions/ReadEquipmentInduction'));
const EmailVerification = lazyPage(() => import('./pages/EmailVerification'));
const ConfirmEmailChange = lazyPage(() => import('./pages/ConfirmEmailChange'));
const ResendVerification = lazyPage(() => import('./pages/ResendVerification'));
const ForgotPassword = lazyPage(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyPage(() => import('./pages/ResetPassword'));
import MfaChallenge from './pages/MfaChallenge';
const MfaSettings = lazyPage(() => import('./pages/settings/MfaSettings'));
const OrgSecuritySettings = lazyPage(() => import('./pages/admin/OrgSecuritySettings'));
const WebsitesSettings = lazyPage(() => import('./pages/admin/WebsitesSettings'));
const CropTypesAdmin = lazyPage(() => import('./pages/admin/CropTypesAdmin'));
const CultivarAdmin = lazyPage(() => import('./pages/cultivar/CultivarAdmin'));
const HomeGardenCare = lazyPage(() => import('./pages/cultivar/HomeGardenCare'));
const ProgramsAdmin = lazyPage(() => import('./pages/cultivar/ProgramsAdmin'));
const ProgramPriceVersions = lazyPage(() => import('./pages/cultivar/ProgramPriceVersions'));
const ProgramContractTemplates = lazyPage(() => import('./pages/cultivar/ProgramContractTemplates'));
const OrderList = lazyPage(() => import('./pages/cultivar/OrderList'));
const OrderCreate = lazyPage(() => import('./pages/cultivar/OrderCreate'));
const OrderDetail = lazyPage(() => import('./pages/cultivar/OrderDetail'));
const ProgramApprovers = lazyPage(() => import('./pages/cultivar/ProgramApprovers'));
const ProgramAgreements = lazyPage(() => import('./pages/cultivar/ProgramAgreements'));
const ProgramCharges = lazyPage(() => import('./pages/cultivar/ProgramCharges'));
const ApprovalQueue = lazyPage(() => import('./pages/cultivar/ApprovalQueue'));
const SupplyClaimQueue = lazyPage(() => import('./pages/cultivar/SupplyClaimQueue'));
const NurseryOffers = lazyPage(() => import('./pages/cultivar/NurseryOffers'));
const SublicenceGroups = lazyPage(() => import('./pages/cultivar/SublicenceGroups'));
const OrderApiKeys = lazyPage(() => import('./pages/cultivar/OrderApiKeys'));
const AttributeDefinitionsManager = lazyPage(() => import('./pages/cultivar/AttributeDefinitionsManager'));
const GrowingRegionsManager = lazyPage(() => import('./pages/cultivar/GrowingRegionsManager'));
const MaturityChart = lazyPage(() => import('./pages/cultivar/MaturityChart'));
const Approvals = lazyPage(() => import('./pages/Approvals'));
const ApprovalsPeriods = lazyPage(() => import('./pages/ApprovalsPeriods'));
const PayrollManager = lazyPage(() => import('./pages/PayrollManager'));
const PayrollPeriods = lazyPage(() => import('./pages/PayrollPeriods'));
const WorkScheduleSettings = lazyPage(() => import('./pages/WorkScheduleSettings'));
const PayrollExportMappings = lazyPage(() => import('./pages/PayrollExportMappings'));
const MyobSettings = lazyPage(() => import('./pages/MyobSettings'));
const PublicHolidaySettings = lazyPage(() => import('./pages/PublicHolidaySettings'));
const IrrigationOverview = lazyPage(() => import('./pages/irrigation/IrrigationOverview'));
const EspNowDashboard = lazyPage(() => import('./pages/irrigation/EspNowDashboard'));
const FarmIrrigationDetail = lazyPage(() => import('./pages/irrigation/FarmIrrigationDetail'));
const EspNowControllerDetail = lazyPage(() => import('./pages/irrigation/EspNowControllerDetail'));
const EspNowAdminSettings = lazyPage(() => import('./pages/irrigation/EspNowAdminSettings'));
const IrrigationPeopleFarms = lazyPage(() => import('./pages/irrigation/IrrigationPeopleFarms'));
import NotForFarmViewers from './components/irrigation/NotForFarmViewers';
const NetworkMap = lazyPage(() => import('./pages/irrigation/NetworkMap'));
const SensorDeviceDetail = lazyPage(() => import('./pages/irrigation/SensorDeviceDetail'));

// Orchard Management Pages
const OrchardDashboard = lazyPage(() => import('./pages/orchard/OrchardDashboard'));
const FarmCreate = lazyPage(() => import('./pages/orchard/FarmCreate'));
const FarmDetail = lazyPage(() => import('./pages/orchard/FarmDetail'));
const FarmEdit = lazyPage(() => import('./pages/orchard/FarmEdit'));
const BlockCreate = lazyPage(() => import('./pages/orchard/BlockCreate'));
const BlockDetail = lazyPage(() => import('./pages/orchard/BlockDetail'));
const BlockEdit = lazyPage(() => import('./pages/orchard/BlockEdit'));
const CultivarAreaCreate = lazyPage(() => import('./pages/orchard/CultivarAreaCreate'));
const CultivarAreaDetail = lazyPage(() => import('./pages/orchard/CultivarAreaDetail'));
const CropLoadCount = lazyPage(() => import('./pages/orchard/CropLoadCount'));
const CultivarAreaEdit = lazyPage(() => import('./pages/orchard/CultivarAreaEdit'));
const SoilAnalysis = lazyPage(() => import('./pages/orchard/SoilAnalysis'));
const SoilSampleForm = lazyPage(() => import('./pages/orchard/SoilSampleForm'));
const SoilSampleDetail = lazyPage(() => import('./pages/orchard/SoilSampleDetail'));
const NutrientRemoval = lazyPage(() => import('./pages/orchard/NutrientRemoval'));
const Fertiliser = lazyPage(() => import('./pages/orchard/Fertiliser'));
const FertiliserPlanPage = lazyPage(() => import('./pages/orchard/FertiliserPlanPage'));

// Subscription Pages
const SubscriptionManagement = lazyPage(() => import('./pages/subscriptions/SubscriptionManagement'));
const SubscriptionLicenseManagement = lazyPage(() => import('./pages/subscriptions/LicenseManagement'));
const BillingConsole = lazyPage(() => import('./pages/billing/BillingConsole'));
const BillingOnboard = lazyPage(() => import('./pages/billing/BillingOnboard'));
const BillingOrganisation = lazyPage(() => import('./pages/billing/BillingOrganisation'));
const PurchaseFlow = lazyPage(() => import('./pages/subscriptions/PurchaseFlow'));
const InvoiceHistory = lazyPage(() => import('./pages/subscriptions/InvoiceHistory'));
const XeroAdmin = lazyPage(() => import('./pages/subscriptions/XeroAdmin'));
const GuideIndex = lazyPage(() => import('./pages/guides/GuideIndex'));
const GuidePage = lazyPage(() => import('./pages/guides/GuidePage'));

// Equipment Pages
const EquipmentList          = lazyPage(() => import('./pages/equipment/EquipmentList'));
const EquipmentDetail        = lazyPage(() => import('./pages/equipment/EquipmentDetail'));
const EquipmentForm          = lazyPage(() => import('./pages/equipment/EquipmentForm'));
const PreStartFlow           = lazyPage(() => import('./pages/equipment/PreStartFlow'));
import TrackingWatcher        from './components/TrackingWatcher';
import PresenceWatcher        from './components/PresenceWatcher';
import IncomingCallWatcher    from './components/sales/IncomingCallWatcher';
import LocationDisclosureHost from './components/LocationDisclosureHost';
const MaintenanceManagement  = lazyPage(() => import('./pages/equipment/MaintenanceManagement'));
const AttachImplement        = lazyPage(() => import('./pages/equipment/AttachImplement'));
const SessionHistory         = lazyPage(() => import('./pages/equipment/SessionHistory'));
const ServiceRecords         = lazyPage(() => import('./pages/equipment/ServiceRecords'));
const EquipmentAdminSettings = lazyPage(() => import('./pages/equipment/EquipmentAdminSettings'));
const PartsCatalog           = lazyPage(() => import('./pages/equipment/PartsCatalog'));
const InsurancePolicies      = lazyPage(() => import('./pages/equipment/InsurancePolicies'));
const InsurancePolicyDetail  = lazyPage(() => import('./pages/equipment/InsurancePolicyDetail'));
const InsurancePolicyForm    = lazyPage(() => import('./pages/equipment/InsurancePolicyForm'));

// Spray Management Pages
const SprayDashboard      = lazyPage(() => import('./pages/spray/SprayDashboard'));
const SprayCreate         = lazyPage(() => import('./pages/spray/SprayCreate'));
const SprayDetail         = lazyPage(() => import('./pages/spray/SprayDetail'));
const SprayEdit                = lazyPage(() => import('./pages/spray/SprayEdit'));
const ChemicalRegistry         = lazyPage(() => import('./pages/spray/ChemicalRegistry'));
const ChemicalDetail           = lazyPage(() => import('./pages/spray/ChemicalDetail'));
const InventoryDashboard       = lazyPage(() => import('./pages/spray/InventoryDashboard'));
const SprayAdminSettings       = lazyPage(() => import('./pages/spray/SprayAdminSettings'));
const PestDiseaseAdmin         = lazyPage(() => import('./pages/spray/PestDiseaseAdmin'));
const PestDiseaseDetail        = lazyPage(() => import('./pages/spray/PestDiseaseDetail'));
const PurchaseOrderList         = lazyPage(() => import('./pages/purchaseOrders/PurchaseOrderList'));
const PurchaseOrderCreate       = lazyPage(() => import('./pages/purchaseOrders/PurchaseOrderCreate'));
const PurchaseOrderDetail       = lazyPage(() => import('./pages/purchaseOrders/PurchaseOrderDetail'));
import PurchaseOrdersGate        from './components/purchaseOrders/PurchaseOrdersGate';
const SprayMyCredentials        = lazyPage(() => import('./pages/spray/SprayMyCredentials'));
const SprayCalibration          = lazyPage(() => import('./pages/spray/SprayCalibration'));
import SprayTncGate               from './components/spray/SprayTncGate';

// Weather Stations Pages
const WeatherPage = lazyPage(() => import('./pages/weather/WeatherPage'));
const WeatherStationSetup = lazyPage(() => import('./pages/weather/WeatherStationSetup'));
const ChillUnitsPage = lazyPage(() => import('./pages/weather/ChillUnitsPage'));
const PestRiskDashboard = lazyPage(() => import('./pages/weather/PestRiskDashboard'));

// Farm Jobs Pages
const JobsDashboard    = lazyPage(() => import('./pages/jobs/JobsDashboard'));
const JobCreate        = lazyPage(() => import('./pages/jobs/JobCreate'));
const JobDetail        = lazyPage(() => import('./pages/jobs/JobDetail'));
const JobEdit          = lazyPage(() => import('./pages/jobs/JobEdit'));
const JobOperatorView  = lazyPage(() => import('./pages/jobs/JobOperatorView'));
const Workforce        = lazyPage(() => import('./pages/jobs/Workforce'));
const Labour           = lazyPage(() => import('./pages/jobs/Labour'));
const Insights         = lazyPage(() => import('./pages/jobs/Insights'));
const CrewMode         = lazyPage(() => import('./pages/jobs/CrewMode'));
const PickBins         = lazyPage(() => import('./pages/harvest/PickBins'));
const Shed             = lazyPage(() => import('./pages/harvest/Shed'));

// Breeder Pages
const BreederDashboard = lazyPage(() => import('./pages/breeder/BreederDashboard'));
const BreederPlantingMap = lazyPage(() => import('./pages/breeder/BreederPlantingMap'));
const BreederLicensors = lazyPage(() => import('./pages/breeder/BreederLicensors'));

// Nursery Pages
const NurseryDashboard = lazyPage(() => import('./pages/nursery/NurseryDashboard'));
const NurseryLocations = lazyPage(() => import('./pages/nursery/NurseryLocations'));
const TreeTypeAdmin = lazyPage(() => import('./pages/nursery/TreeTypeAdmin'));
const PropagationSetup = lazyPage(() => import('./pages/nursery/PropagationSetup'));
const QuarantineProtocols = lazyPage(() => import('./pages/nursery/QuarantineProtocols'));
const MaterialList = lazyPage(() => import('./pages/nursery/MaterialList'));
const MaterialForm = lazyPage(() => import('./pages/nursery/MaterialForm'));
const MaterialDetail = lazyPage(() => import('./pages/nursery/MaterialDetail'));
const MotherPlants = lazyPage(() => import('./pages/nursery/MotherPlants'));
const BuddingList = lazyPage(() => import('./pages/nursery/BuddingList'));
const ProductionBoard = lazyPage(() => import('./pages/nursery/ProductionBoard'));
const LotList = lazyPage(() => import('./pages/nursery/LotList'));
const LotCreate = lazyPage(() => import('./pages/nursery/LotCreate'));
const LotDetail = lazyPage(() => import('./pages/nursery/LotDetail'));
const StockItemList = lazyPage(() => import('./pages/nursery/StockItemList'));
const StockItemForm = lazyPage(() => import('./pages/nursery/StockItemForm'));
const NurseryProductList = lazyPage(() => import('./pages/nursery/ProductList'));
const NurseryProductForm = lazyPage(() => import('./pages/nursery/ProductForm'));
const NurseryOrderList = lazyPage(() => import('./pages/nursery/OrderList'));
const NurseryOrderCreate = lazyPage(() => import('./pages/nursery/OrderCreate'));
const NurseryOrderDetail = lazyPage(() => import('./pages/nursery/OrderDetail'));
const NurseryOrderDrawDown = lazyPage(() => import('./pages/nursery/OrderDrawDown'));
const NurseryPoImport = lazyPage(() => import('./pages/nursery/PoImport'));
const NurseryCrossDockSchedules = lazyPage(() => import('./pages/nursery/CrossDockSchedules'));
const NurseryCrossDockScheduleDetail = lazyPage(() => import('./pages/nursery/CrossDockScheduleDetail'));
const NurseryRetailerRange = lazyPage(() => import('./pages/nursery/RetailerRange'));
const NurseryRetailerHub = lazyPage(() => import('./pages/nursery/RetailerHub'));
const NurseryStockHub = lazyPage(() => import('./pages/nursery/StockHub'));
const NurseryLogisticsHub = lazyPage(() => import('./pages/nursery/LogisticsHub'));
const NurserySalesHub = lazyPage(() => import('./pages/nursery/SalesHub'));
const NurseryBackorders = lazyPage(() => import('./pages/nursery/Backorders'));
const NurseryWorkSummary = lazyPage(() => import('./pages/nursery/WorkSummary'));
const NurseryInventoryDashboard = lazyPage(() => import('./pages/nursery/InventoryDashboard'));
const NurseryStockSummary = lazyPage(() => import('./pages/nursery/StockSummary'));
const NurseryStockItemSummary = lazyPage(() => import('./pages/nursery/StockItemSummary'));
const CustomerOrdersDashboard = lazyPage(() => import('./pages/nursery/CustomerOrdersDashboard'));
const AccountsDashboard = lazyPage(() => import('./pages/accounts/AccountsDashboard'));
const RetailDashboard = lazyPage(() => import('./pages/retail/RetailDashboard'));
const SalesHome = lazyPage(() => import('./pages/sales/SalesHome'));
const PhoneSettings = lazyPage(() => import('./pages/sales/PhoneSettings'));
const SalesFollowUps = lazyPage(() => import('./pages/sales/FollowUps'));
import { CRM_ALSO } from './hooks/useCrmSeat';
const NurseryCustomerList = lazyPage(() => import('./pages/accounts/CustomerList'));
const NurseryCustomerForm = lazyPage(() => import('./pages/accounts/CustomerForm'));
const NurseryCustomerDetail = lazyPage(() => import('./pages/accounts/CustomerDetail'));
const NurseryInvoiceList = lazyPage(() => import('./pages/accounts/InvoiceList'));
const NurseryInvoiceDetail = lazyPage(() => import('./pages/accounts/InvoiceDetail'));
const NurseryReceivables = lazyPage(() => import('./pages/accounts/Receivables'));
const NurseryRebates = lazyPage(() => import('./pages/accounts/Rebates'));
const Remittances = lazyPage(() => import('./pages/accounts/Remittances'));
const XeroSettings = lazyPage(() => import('./pages/accounts/XeroSettings'));
const NurseryPriceListList = lazyPage(() => import('./pages/accounts/PriceListList'));
const NurseryPriceListForm = lazyPage(() => import('./pages/accounts/PriceListForm'));
const NurseryWebsiteSignupsList = lazyPage(() => import('./pages/nursery/WebsiteSignupsList'));
// TEMPORARY: delete after 2026-10-28 with its route below.
const LegacyCustomerImport = lazyPage(() => import('./pages/nursery/LegacyCustomerImport'));
const LegacyCultivarImport = lazyPage(() => import('./pages/cultivar/LegacyCultivarImport'));
const NurseryTradeAccountRequestsList = lazyPage(() => import('./pages/nursery/TradeAccountRequestsList'));
const NurseryDeliveryZonesList = lazyPage(() => import('./pages/nursery/DeliveryZonesList'));
const NurseryPickers = lazyPage(() => import('./pages/nursery/NurseryPickers'));
const NurseryDespatchPlanner = lazyPage(() => import('./pages/nursery/DespatchPlanner'));
const NurseryLoadList = lazyPage(() => import('./pages/nursery/LoadList'));
const NurseryLoadDetail = lazyPage(() => import('./pages/nursery/LoadDetail'));
const NurseryPickingLoads = lazyPage(() => import('./pages/nursery/PickingLoads'));
const NurseryDeliveries = lazyPage(() => import('./pages/nursery/Deliveries'));
const NurseryDeliveryRun = lazyPage(() => import('./pages/nursery/DeliveryRun'));
const NurseryDeliverStop = lazyPage(() => import('./pages/nursery/DeliverStop'));
const NurseryPickList = lazyPage(() => import('./pages/nursery/PickListPage'));
const NurseryTransferList = lazyPage(() => import('./pages/nursery/TransferList'));
const NurseryStockToMove = lazyPage(() => import('./pages/nursery/StockToMove'));
const NurseryStillages = lazyPage(() => import('./pages/nursery/Stillages'));
const NurseryTransferDetail = lazyPage(() => import('./pages/nursery/TransferDetail'));
const NurseryDespatchPlaces = lazyPage(() => import('./pages/nursery/DespatchPlaces'));
const NurseryTills = lazyPage(() => import('./pages/nursery/Tills'));
const NurseryTillDetail = lazyPage(() => import('./pages/nursery/TillDetail'));
const CardPayments = lazyPage(() => import('./pages/accounts/CardPayments'));
const TradingTerms = lazyPage(() => import('./pages/accounts/TradingTerms'));
const MonthlyStatements = lazyPage(() => import('./pages/accounts/MonthlyStatements'));
const BasSummary = lazyPage(() => import('./pages/accounts/BasSummary'));
const Budget = lazyPage(() => import('./pages/accounts/Budget'));
const UpstreamAgreements = lazyPage(() => import('./pages/cultivar/UpstreamAgreements'));
const UpstreamStatementView = lazyPage(() => import('./pages/cultivar/UpstreamStatementView'));
const AccountRules = lazyPage(() => import('./pages/accounts/AccountRules'));
const Deposits = lazyPage(() => import('./pages/accounts/Deposits'));
const LegacyBalances = lazyPage(() => import('./pages/accounts/LegacyBalances'));
const Suppliers = lazyPage(() => import('./pages/accounts/Suppliers'));
const PurchaseOrderPeople = lazyPage(() => import('./pages/accounts/PurchaseOrderPeople'));
const CreditNoteRequests = lazyPage(() => import('./pages/accounts/CreditNoteRequests'));
const CounterPaid = lazyPage(() => import('./pages/CounterPaid'));
const NurseryStocktakes = lazyPage(() => import('./pages/nursery/Stocktakes'));
const NurserySprays = lazyPage(() => import('./pages/nursery/NurserySprays'));
const NurseryStocktakeCount = lazyPage(() => import('./pages/nursery/StocktakeCount'));
const NurseryForecast = lazyPage(() => import('./pages/nursery/NurseryForecast'));
const NurseryColourCodes = lazyPage(() => import('./pages/nursery/ColourCodes'));
const NurseryPainting = lazyPage(() => import('./pages/nursery/Painting'));

// Cultivar Registry Pages
const CultivarRegistry = lazyPage(() => import('./pages/cultivar/CultivarRegistry'));
const CultivarCreate = lazyPage(() => import('./pages/cultivar/CultivarCreate'));
const QuickEvaluations = lazyPage(() => import('./pages/cultivar/QuickEvaluations'));
const PbrDashboard = lazyPage(() => import('./pages/cultivar/pbr/PbrDashboard'));
const PbrCandidates = lazyPage(() => import('./pages/cultivar/pbr/PbrCandidates'));
const PbrApplications = lazyPage(() => import('./pages/cultivar/pbr/PbrApplications'));
const PbrApplicationCreate = lazyPage(() => import('./pages/cultivar/pbr/PbrApplicationCreate'));
const PbrApplicationDetail = lazyPage(() => import('./pages/cultivar/pbr/PbrApplicationDetail'));
const QualifiedPersonsRegister = lazyPage(() => import('./pages/cultivar/pbr/QualifiedPersonsRegister'));
const CultivarEdit = lazyPage(() => import('./pages/cultivar/CultivarEdit'));
const CultivarDetail = lazyPage(() => import('./pages/cultivar/CultivarDetail'));
const LicenseManagement = lazyPage(() => import('./pages/cultivar/LicenseManagement'));
const DeclarationManagement = lazyPage(() => import('./pages/cultivar/DeclarationManagement'));
const CertificateManagement = lazyPage(() => import('./pages/cultivar/CertificateManagement'));
const ComplianceDashboard = lazyPage(() => import('./pages/cultivar/ComplianceDashboard'));
const VerificationPage = lazyPage(() => import('./pages/cultivar/VerificationPage'));
const ContractsList = lazyPage(() => import('./pages/cultivar/ContractsList'));
const ContractDetail = lazyPage(() => import('./pages/cultivar/ContractDetail'));
const ContractsHistory = lazyPage(() => import('./pages/cultivar/ContractsHistory'));
const LicenseVerify = lazyPage(() => import('./pages/cultivar/LicenseVerify'));
const LicenseAgreementView = lazyPage(() => import('./pages/cultivar/LicenseAgreementView'));
const LicensorDashboard = lazyPage(() => import('./pages/cultivar/LicensorDashboard'));
const IssueLicense = lazyPage(() => import('./pages/cultivar/IssueLicense'));
const RecordSignedAgreement = lazyPage(() => import('./pages/cultivar/RecordSignedAgreement'));


function DeepLinkHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    const listener = CapApp.addListener('appUrlOpen', (event: { url: string }) => {
      const url = new URL(event.url);
      if (url.pathname) {
        navigate(url.pathname);
      }
    });
    return () => { listener.then(l => l.remove()); };
  }, [navigate]);

  // Live (over-the-air) web-bundle updates. Checked on launch and whenever the
  // app returns to the foreground; a new bundle is staged silently and applies
  // on the next cold start, never mid-session. Rate-limited inside the service.
  useEffect(() => {
    checkForUpdate();
    const sub = CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) checkForUpdate();
    });
    return () => { sub.then(l => l.remove()); };
  }, []);

  usePushNotifications();
  useTimesheetReminder();
  return null;
}

/**
 * Forces a full unmount+remount of any component whenever the active org changes.
 * This guarantees every piece of local state is cleared — more reliable than
 * dep-array tracking alone, which can silently fail if context updates batch
 * or if the component was already showing data for the same orgId value.
 */
function useActiveOrgId() {
  const { user } = useAuth();
  return user?.orgs?.find(o => o.is_active)?.id ?? user?.organisation_id ?? 'no-org';
}

function OrgScopedDashboard() {
  const activeOrgId = useActiveOrgId();
  return <Dashboard key={activeOrgId} />;
}

function OrgScopedTimesheet() {
  const activeOrgId = useActiveOrgId();
  return <TimesheetHome key={activeOrgId} />;
}

function OrgScopedTimesheetHistory() {
  const activeOrgId = useActiveOrgId();
  return <TimesheetHistoryPage key={activeOrgId} />;
}

function OrgScopedMyTimesheet() {
  const activeOrgId = useActiveOrgId();
  return <MyTimesheet key={activeOrgId} />;
}


function PageLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Invalidate any active React Query data when a pull-to-refresh fires.
  // Pages using useEffect-based fetching should subscribe via useAppRefresh().
  useEffect(() => {
    const handler = () => {
      queryClient.refetchQueries({ type: 'active' });
    };
    window.addEventListener(APP_REFRESH_EVENT, handler);
    return () => window.removeEventListener(APP_REFRESH_EVENT, handler);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LanguageSync />
        <Router>
          <ScrollToTop />
          <PullToRefresh />
          <DeepLinkHandler />
          <TrackingWatcher />
          <PresenceWatcher />
          <IncomingCallWatcher />
          <LocationDisclosureHost />
          <OrgChangedNotice />
          <UpdateAvailableBanner />
          <PlatformTermsGate />
          <InductionGate />
          <ModuleTermsGate />
          <div className="App">
            {/* Pages load when opened (lazyPage). The shell above stays in the entry chunk. */}
            <Suspense fallback={<PageLoading />}>
            <Routes>
              {/* Public routes — redirect to login on native mobile apps */}
              <Route
                path="/"
                element={Capacitor.isNativePlatform() ? <Navigate to="/login" replace /> : <Landing />}
              />
              <Route
                path="/features/:slug"
                element={Capacitor.isNativePlatform() ? <Navigate to="/login" replace /> : <FeaturePage />}
              />
              <Route path="/features" element={<Navigate to="/#features" replace />} />
              <Route
                path="/pricing"
                element={Capacitor.isNativePlatform() ? <Navigate to="/login" replace /> : <Pricing />}
              />
              
              {/* Auth routes - redirect to dashboard if already authenticated */}
              <Route 
                path="/login" 
                element={
                  <ProtectedRoute requireAuth={false}>
                    <LoginForm />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/register" 
                element={
                  <ProtectedRoute requireAuth={false}>
                    <RegisterForm />
                  </ProtectedRoute>
                } 
              />
              
              {/* Email verification and password reset routes */}
              <Route path="/verify-email" element={<EmailVerification />} />
              <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
              <Route path="/resend-verification" element={<ResendVerification />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* MFA challenge — unauthenticated; gated by mfaTicket in route state */}
              <Route path="/mfa-challenge" element={<MfaChallenge />} />

              {/* MFA self-service settings */}
              <Route
                path="/settings/security"
                element={
                  <ProtectedRoute>
                    <MfaSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/security"
                element={
                  <ProtectedRoute>
                    <OrgSecuritySettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/websites"
                element={
                  <ProtectedRoute>
                    <WebsitesSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/admin"
                element={
                  <ProtectedRoute>
                    <CultivarAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/admin/home-garden"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry" licenseType={['pbr', 'admin']}>
                      <HomeGardenCare />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/admin/crop-types"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry" licenseType="admin">
                      <CropTypesAdmin />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/admin/programs"
                element={
                  <ProtectedRoute>
                    <ProgramsAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/admin/programs/:id/price-versions"
                element={
                  <ProtectedRoute>
                    <ProgramPriceVersions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/admin/programs/:id/contract-templates"
                element={
                  <ProtectedRoute>
                    <ProgramContractTemplates />
                  </ProtectedRoute>
                }
              />
              <Route path="/cultivar/orders"     element={<ProtectedRoute><OrderList /></ProtectedRoute>} />
              <Route path="/cultivar/orders/new" element={<ProtectedRoute><OrderCreate /></ProtectedRoute>} />
              <Route path="/cultivar/orders/:id" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
              <Route path="/cultivar/admin/programs/:id/approvers" element={<ProtectedRoute><ProgramApprovers /></ProtectedRoute>} />
              <Route path="/cultivar/admin/programs/:id/agreements" element={<ProtectedRoute><ProgramAgreements /></ProtectedRoute>} />
              <Route path="/cultivar/admin/programs/:id/charges" element={<ProtectedRoute><ProgramCharges /></ProtectedRoute>} />
              <Route path="/cultivar/approvals" element={<ProtectedRoute><ApprovalQueue /></ProtectedRoute>} />
              <Route path="/cultivar/supply-claims" element={<ProtectedRoute><SupplyClaimQueue /></ProtectedRoute>} />
              <Route path="/cultivar/nursery-offers" element={<ProtectedRoute><NurseryOffers /></ProtectedRoute>} />
              <Route path="/cultivar/sublicence-groups" element={<ProtectedRoute><SublicenceGroups /></ProtectedRoute>} />
              <Route path="/cultivar/maturity-chart" element={<ProtectedRoute><MaturityChart /></ProtectedRoute>} />
              <Route path="/cultivar/orders/api-keys" element={<ProtectedRoute><OrderApiKeys /></ProtectedRoute>} />
              <Route
                path="/cultivar/admin/growing-regions"
                element={
                  <ProtectedRoute>
                    <GrowingRegionsManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/admin/attributes"
                element={
                  <ProtectedRoute>
                    <AttributeDefinitionsManager />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <OrgScopedDashboard />
                  </ProtectedRoute>
                }
              />
              
              <Route 
                path="/timesheet" 
                element={
                  <ProtectedRoute>
                    <OrgScopedTimesheet />
                  </ProtectedRoute>
                } 
              />
              
              <Route
                path="/timesheet/history"
                element={
                  <ProtectedRoute>
                    <OrgScopedTimesheetHistory />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/timesheet/my-timesheet"
                element={
                  <ProtectedRoute>
                    <OrgScopedMyTimesheet />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/work/resume"
                element={
                  <ProtectedRoute>
                    <ResumeWork />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/approvals"
                element={
                  <ProtectedRoute>
                    <Approvals />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/approvals/periods"
                element={
                  <ProtectedRoute>
                    <ApprovalsPeriods />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/payroll"
                element={
                  <ProtectedRoute>
                    <PayrollManager />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/payroll/periods"
                element={
                  <ProtectedRoute>
                    <PayrollPeriods />
                  </ProtectedRoute>
                }
              />

              {/* An invitation sent to an address that already has an account */}
              <Route
                path="/join-organisation/:token"
                element={
                  <ProtectedRoute>
                    <JoinOrganisation />
                  </ProtectedRoute>
                }
              />

              {/* Admin routes */}

              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminManagement />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/super-admin"
                element={
                  <ProtectedRoute>
                    <SuperAdminDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/work-schedule"
                element={
                  <ProtectedRoute>
                    <WorkScheduleSettings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/public-holidays"
                element={
                  <ProtectedRoute>
                    <PublicHolidaySettings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr"
                element={
                  <ProtectedRoute>
                    <HRManagement />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr/live-dashboard"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="timesheet" moduleName="Timesheet" licenseType={['approver', 'hr']}>
                      <HRLiveDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr/pay-cycle-dashboard"
                element={
                  <ProtectedRoute>
                    <PayCycleDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr/pay-periods"
                element={
                  <ProtectedRoute>
                    <PayPeriodShadow />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr/company-policies"
                element={
                  <ProtectedRoute>
                    <CompanyPolicies />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr/staff"
                element={
                  <ProtectedRoute>
                    <StaffProfile />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr/award-reviews"
                element={
                  <ProtectedRoute>
                    <AwardReviews />
                  </ProtectedRoute>
                }
              />

              {/* Leave entitlements moved onto the employee profile's Leave tab. */}
              <Route path="/hr/leave-entitlements" element={<Navigate to="/hr/staff?tab=leave" replace />} />

              {/* Workplace Health & Safety — worker reporting (any authenticated
                  user) + the HR-gated management console. */}
              <Route path="/whs/report" element={<ProtectedRoute><WhsReport /></ProtectedRoute>} />
              <Route path="/whs/track" element={<ProtectedRoute><WhsTrackReport /></ProtectedRoute>} />
              <Route path="/hr/whs" element={<ProtectedRoute><WhsDashboard /></ProtectedRoute>} />
              <Route path="/hr/whs/reports" element={<ProtectedRoute><WhsReports /></ProtectedRoute>} />
              <Route path="/hr/whs/reports/:id" element={<ProtectedRoute><WhsReportDetail /></ProtectedRoute>} />
              <Route path="/hr/whs/risk-assessments" element={<ProtectedRoute><WhsRiskAssessments /></ProtectedRoute>} />
              <Route path="/hr/whs/risk-assessments/:id" element={<ProtectedRoute><WhsRiskAssessmentDetail /></ProtectedRoute>} />
              <Route path="/hr/whs/actions" element={<ProtectedRoute><WhsActions /></ProtectedRoute>} />
              <Route path="/hr/whs/inspections" element={<ProtectedRoute><WhsInspections /></ProtectedRoute>} />
              <Route path="/hr/whs/inspections/:id" element={<ProtectedRoute><WhsInspectionDetail /></ProtectedRoute>} />
              <Route path="/hr/whs/audits" element={<ProtectedRoute><WhsAudits /></ProtectedRoute>} />
              <Route path="/hr/whs/audits/:id" element={<ProtectedRoute><WhsAuditDetail /></ProtectedRoute>} />
              <Route path="/hr/whs/documents" element={<ProtectedRoute><WhsDocuments /></ProtectedRoute>} />
              <Route path="/hr/whs/training" element={<ProtectedRoute><WhsTraining /></ProtectedRoute>} />
              <Route path="/hr/whs/emergency-plans" element={<ProtectedRoute><WhsEmergencyPlans /></ProtectedRoute>} />
              <Route path="/hr/whs/objectives" element={<ProtectedRoute><WhsObjectives /></ProtectedRoute>} />
              <Route path="/hr/whs/management-reviews" element={<ProtectedRoute><WhsManagementReviews /></ProtectedRoute>} />
              <Route path="/hr/whs/settings" element={<ProtectedRoute><WhsSettings /></ProtectedRoute>} />

              {/* Staff Certifications & Licenses register */}
              <Route
                path="/certifications"
                element={
                  <ProtectedRoute>
                    <MyCertifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/certifications/admin"
                element={
                  <ProtectedRoute>
                    <CertificationsAdmin />
                  </ProtectedRoute>
                }
              />

              {/* Inductions. The register pages gate themselves on any of the
                  seats that sign people off (farm_jobs supervisor/admin,
                  vehicles creator/admin) or the HR seat; "My inductions" is
                  every member's own record and needs no seat. */}
              <Route path="/inductions" element={<ProtectedRoute><InductionsHub /></ProtectedRoute>} />
              {/* Compliance & audit — gated in-page on the seats that manage each section. */}
              <Route path="/compliance" element={<ProtectedRoute><Compliance /></ProtectedRoute>} />
              <Route path="/compliance/audits/:id" element={<ProtectedRoute><EngagementDetail /></ProtectedRoute>} />
              <Route path="/inductions/equipment/:id" element={<ProtectedRoute><InductionDetail /></ProtectedRoute>} />
              <Route path="/inductions/people/:ref" element={<ProtectedRoute><InductionPerson /></ProtectedRoute>} />
              <Route path="/my-inductions" element={<ProtectedRoute><MyInductions /></ProtectedRoute>} />
              {/* Your own warnings: no seat, the server scopes them to you. */}
              <Route path="/my-warnings" element={<ProtectedRoute><MyWarnings /></ProtectedRoute>} />
              <Route path="/my-warnings/:id" element={<ProtectedRoute><MyWarnings /></ProtectedRoute>} />
              <Route path="/my-inductions/policies/:id" element={<ProtectedRoute><ReadPolicy /></ProtectedRoute>} />
              <Route path="/my-inductions/equipment/:id" element={<ProtectedRoute><ReadEquipmentInduction /></ProtectedRoute>} />

              {/* Guides: how each module works, to read or print. No seat. */}
              <Route path="/guides" element={<ProtectedRoute><GuideIndex /></ProtectedRoute>} />
              <Route path="/guides/:slug" element={<ProtectedRoute><GuidePage /></ProtectedRoute>} />

              {/* Seats (the page kept its /subscriptions path: links point at it) */}
              <Route
                path="/subscriptions"
                element={
                  <ProtectedRoute>
                    <SubscriptionManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscriptions/:subscriptionId/licenses"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <SubscriptionLicenseManagement />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscriptions/purchase"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <PurchaseFlow />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscriptions/invoices"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <InvoiceHistory />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
              {/* Platform billing console (billing_manager / super_admin). The
                  old super-admin subscriptions page lived at /subscriptions/admin. */}
              <Route path="/subscriptions/admin" element={<Navigate to="/billing" replace />} />
              <Route
                path="/billing"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <BillingConsole />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/onboard"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <BillingOnboard />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/organisations/:orgId"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <BillingOrganisation />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/xero"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <XeroAdmin />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
  
              {/* The old NPK soil-sensor dashboard is gone; sensors live under /irrigation. */}
              <Route path="/soil-sensors" element={<Navigate to="/irrigation" replace />} />
              <Route
                path="/irrigation"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Sensors & Irrigation">
                      <IrrigationOverview />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* One farm's live irrigation — what a grower opens from the
                  overview. Devices sit behind it, not in front of it. */}
              <Route
                path="/irrigation/farms/:farmId"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Sensors & Irrigation">
                      <FarmIrrigationDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/irrigation/network"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Irrigation Network">
                      <NotForFarmViewers><NetworkMap /></NotForFarmViewers>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Which farms each person sees in Irrigation: irrigation admin seat. */}
              <Route
                path="/irrigation/people"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Irrigation" licenseType="admin">
                      <IrrigationPeopleFarms />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* ESP-NOW Irrigation Routes */}
              <Route
                path="/irrigation/espnow/admin"
                element={
                  <ProtectedRoute>
                    {/* Device management needs the irrigation *admin* seat — the
                        same gate the backend registry routes enforce. Without the
                        licenseType a plain irrigation user reaches the page and
                        only finds out on the first 403. */}
                    <LicenseGate
                      moduleKey="irrigation"
                      moduleName="Irrigation Device Management"
                      licenseType="admin"
                    >
                      <EspNowAdminSettings />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/irrigation/espnow/:controllerId"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Irrigation Management">
                      <NotForFarmViewers><EspNowControllerDetail /></NotForFarmViewers>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/irrigation/espnow"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Irrigation Management">
                      <NotForFarmViewers><EspNowDashboard /></NotForFarmViewers>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Sensor Devices (tank level + generic sensors).
                  Device management was consolidated into /irrigation/espnow/admin. */}
              <Route
                path="/irrigation/sensors/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Sensor Devices">
                      <SensorDeviceDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* /irrigation/sensors was merged into /irrigation/espnow — redirect. */}
              <Route
                path="/irrigation/sensors"
                element={<Navigate to="/irrigation/espnow" replace />}
              />

              {/* Farms (spatial setup) — free for every authenticated user.
                  No subscription or license required. Paid gating lives on
                  Spray and Farm Jobs for what you do with the farms. */}
              <Route path="/orchard" element={<ProtectedRoute><OrchardDashboard /></ProtectedRoute>} />
              <Route path="/orchard/fertiliser" element={<ProtectedRoute><Fertiliser /></ProtectedRoute>} />
              <Route path="/orchard/fertiliser/plans/:id" element={<ProtectedRoute><FertiliserPlanPage /></ProtectedRoute>} />
              <Route path="/orchard/soil" element={<ProtectedRoute><SoilAnalysis /></ProtectedRoute>} />
              <Route path="/orchard/soil/new" element={<ProtectedRoute><SoilSampleForm /></ProtectedRoute>} />
              <Route path="/orchard/soil/removal" element={<ProtectedRoute><NutrientRemoval /></ProtectedRoute>} />
              <Route path="/orchard/soil/:id" element={<ProtectedRoute><SoilSampleDetail /></ProtectedRoute>} />
              <Route path="/orchard/soil/:id/edit" element={<ProtectedRoute><SoilSampleForm /></ProtectedRoute>} />
              <Route path="/orchard/farms/new" element={<ProtectedRoute><FarmCreate /></ProtectedRoute>} />
              <Route path="/orchard/farms/:id" element={<ProtectedRoute><FarmDetail /></ProtectedRoute>} />
              <Route path="/orchard/farms/:id/edit" element={<ProtectedRoute><FarmEdit /></ProtectedRoute>} />
              <Route path="/orchard/blocks/new" element={<ProtectedRoute><BlockCreate /></ProtectedRoute>} />
              <Route path="/orchard/blocks/:id" element={<ProtectedRoute><BlockDetail /></ProtectedRoute>} />
              <Route path="/orchard/blocks/:id/edit" element={<ProtectedRoute><BlockEdit /></ProtectedRoute>} />
              <Route path="/orchard/cultivar-areas/new" element={<ProtectedRoute><CultivarAreaCreate /></ProtectedRoute>} />
              <Route path="/orchard/cultivar-areas/:id" element={<ProtectedRoute><CultivarAreaDetail /></ProtectedRoute>} />
              <Route path="/orchard/cultivar-areas/:id/edit" element={<ProtectedRoute><CultivarAreaEdit /></ProtectedRoute>} />
              <Route path="/orchard/cultivar-areas/:id/crop-load/:countId" element={<ProtectedRoute><CropLoadCount /></ProtectedRoute>} />

              {/* Cultivar Registry Routes */}
              <Route
                path="/cultivar"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry" alsoModules={[{ moduleKey: 'sales', licenseType: ['user', 'admin'] }]}>
                      <CultivarRegistry />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/quick-evaluations"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <QuickEvaluations />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/create"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <CultivarCreate />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry" alsoModules={[{ moduleKey: 'sales', licenseType: ['user', 'admin'] }]}>
                      <CultivarDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <CultivarEdit />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* License Management */}
              <Route
                path="/cultivar/licenses"
                element={
                  <ProtectedRoute>
                    <LicenseManagement />
                  </ProtectedRoute>
                }
              />

              {/* Declaration Management */}
              <Route
                path="/cultivar/declarations"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <DeclarationManagement />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Certificate Management */}
              <Route
                path="/cultivar/certificates"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <CertificateManagement />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Compliance Dashboard */}
              <Route
                path="/cultivar/compliance"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <ComplianceDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* PBR Workflow — place BEFORE /cultivar/:id so 'pbr' doesn't match :id */}
              <Route
                path="/cultivar/pbr"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <PbrDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/pbr/candidates"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <PbrCandidates />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/pbr/applications"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <PbrApplications />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/pbr/applications/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <PbrApplicationCreate />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/pbr/applications/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <PbrApplicationDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/pbr/qualified-persons"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <QualifiedPersonsRegister />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* PUBLIC Certificate Verification (no auth required) */}
              <Route path="/verify/:token" element={<VerificationPage />} />

              {/* PUBLIC Privacy Policy — reachable by app stores and unauthenticated users */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              {/* PUBLIC: where a customer's phone lands after paying at the counter by QR code */}
              <Route path="/paid" element={<CounterPaid />} />
              {/* PUBLIC external auditor portal: link + emailed code, no account */}
              <Route path="/audit/:token" element={<AuditorPortal />} />
              {/* PUBLIC: a nursery customer's invite: set up an account, or check their details with none */}
              <Route path="/customer-invite/:token" element={<CustomerInvite />} />

              {/* Licensing workflow */}
              {/* Contracts replaced licence templates; old links land on the list. */}
              <Route path="/cultivar/licence-templates" element={<Navigate to="/cultivar/contracts" replace />} />
              {/* TEMPORARY: the legacy cultivar import, switched off 2026-10-28.
                  It checks its own Cultivar Admin seat. */}
              <Route path="/cultivar/legacy-import" element={
                <ProtectedRoute>
                  <LegacyCultivarImport />
                </ProtectedRoute>
              } />
              {/* Gated in the page: a Cultivar PBR/Admin seat or any Accounts seat. */}
              <Route path="/cultivar/upstream" element={<ProtectedRoute><UpstreamAgreements /></ProtectedRoute>} />
              <Route path="/cultivar/upstream/statements/:id" element={<ProtectedRoute><UpstreamStatementView /></ProtectedRoute>} />
              <Route path="/cultivar/contracts" element={
                <ProtectedRoute>
                  <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                    <ContractsList />
                  </LicenseGate>
                </ProtectedRoute>
              } />
              <Route path="/cultivar/contracts/history" element={
                <ProtectedRoute>
                  <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                    <ContractsHistory />
                  </LicenseGate>
                </ProtectedRoute>
              } />
              <Route path="/cultivar/contracts/:id" element={
                <ProtectedRoute>
                  <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                    <ContractDetail />
                  </LicenseGate>
                </ProtectedRoute>
              } />
              {/* PUBLIC: the QR code on every licence PDF points here. */}
              <Route path="/cultivar/verify-license/:token" element={<LicenseVerify />} />
              <Route path="/cultivar/licences/record" element={
                <ProtectedRoute>
                  <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                    <RecordSignedAgreement />
                  </LicenseGate>
                </ProtectedRoute>
              } />
              <Route path="/cultivar/licences/:id/agreement" element={
                <ProtectedRoute>
                  <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                    <LicenseAgreementView />
                  </LicenseGate>
                </ProtectedRoute>
              } />
              <Route path="/cultivar/licensor" element={
                <ProtectedRoute>
                  <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                    <LicensorDashboard />
                  </LicenseGate>
                </ProtectedRoute>
              } />
              <Route path="/cultivar/:id/licences/new" element={
                <ProtectedRoute>
                  <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                    <IssueLicense />
                  </LicenseGate>
                </ProtectedRoute>
              } />

              {/* Spray Management Routes. SprayTncGate overlays the blocking T&C
                  acceptance modal on any spray page until the user accepts the
                  current active version. Backend write endpoints also enforce
                  this via the requireSprayTncAccepted middleware. */}
              <Route
                path="/spray"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayDashboard /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayCreate /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/chemicals"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <ChemicalRegistry />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/chemicals/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <ChemicalDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/inventory"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><InventoryDashboard /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/admin"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayAdminSettings /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/credentials/me"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayMyCredentials /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* Purchase orders are their own section, off the Dashboard. A spray
                  Operator or Creator raises, sends and receives; an Accounts
                  seat reads. They used to be a tab of Spray. */}
              <Route
                path="/purchase-orders"
                element={
                  <ProtectedRoute>
                    <PurchaseOrdersGate><PurchaseOrderList /></PurchaseOrdersGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/purchase-orders/new"
                element={
                  <ProtectedRoute>
                    <PurchaseOrdersGate raising><PurchaseOrderCreate /></PurchaseOrdersGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/purchase-orders/:id"
                element={
                  <ProtectedRoute>
                    <PurchaseOrdersGate><PurchaseOrderDetail /></PurchaseOrdersGate>
                  </ProtectedRoute>
                }
              />
              <Route path="/spray/purchase-orders" element={<MovedRoute to="/purchase-orders" />} />
              <Route path="/spray/purchase-orders/new" element={<MovedRoute to="/purchase-orders/new" />} />
              <Route path="/spray/purchase-orders/:id" element={<MovedRoute to="/purchase-orders/:id" />} />
              <Route
                path="/spray/pests"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><PestRiskDashboard inSpray /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/pests/manage"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><PestDiseaseAdmin /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/pests/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><PestDiseaseDetail /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/pests/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><PestDiseaseDetail /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* Calibrating a machine on its own, with no spray attached. The
                  T&C gate is needed as much as anywhere else: recording a
                  calibration is a write, and the backend enforces the same
                  requireSprayTncAccepted on it. */}
              <Route
                path="/spray/calibration"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayCalibration /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayDetail /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayEdit /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Weather Routes — forecast is free, station data needs the subscription */}
              <Route
                path="/weather"
                element={
                  <ProtectedRoute>
                    <WeatherPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/weather/:stationId"
                element={
                  <ProtectedRoute>
                    <WeatherPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/weather/chill"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="weather_stations" moduleName="Weather Stations">
                      <ChillUnitsPage />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/weather/pests"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <PestRiskDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/weather-stations/setup"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="weather_stations" moduleName="Weather Stations">
                      <WeatherStationSetup />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Farm Jobs Routes */}
              <Route
                path="/jobs"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs">
                      <JobsDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/workforce"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs" licenseType={['supervisor', 'creator', 'admin']}>
                      <Workforce />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* Pickers have no seat: the API lets anyone on the job start bins. */}
              <Route
                path="/harvest/pick"
                element={
                  <ProtectedRoute>
                    <PickBins />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/harvest/shed"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs" licenseType={['supervisor', 'creator', 'admin']}>
                      <Shed />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/insights"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs" licenseType={['supervisor', 'creator', 'admin']}>
                      <Insights />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/labour"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs" licenseType={['supervisor', 'creator', 'admin']}>
                      <Labour />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/:id/crew"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs" licenseType={['supervisor', 'creator', 'admin']}>
                      <CrewMode />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs">
                      <JobCreate />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs">
                      <JobDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs">
                      <JobEdit />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/:id/operate"
                element={
                  // No seat: anyone may run a job they are on (a pre-start's
                  // quick job above all). The server refuses anyone else's.
                  <ProtectedRoute>
                    <JobOperatorView />
                  </ProtectedRoute>
                }
              />

              {/* Breeder / Variety Owner */}
              <Route
                path="/breeder"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="breeder" moduleName="Breeder / Variety Owner">
                      <BreederDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/breeder/licensors"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="breeder" moduleName="Breeder / Variety Owner">
                      <BreederLicensors />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/breeder/cultivars/:id/map"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="breeder" moduleName="Breeder / Variety Owner">
                      <BreederPlantingMap />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Nursery Routes */}
              <Route
                path="/nursery"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('operate')}>
                      <NurseryDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/production"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <ProductionBoard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/lots"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('operate')}>
                      <LotList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/lots/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('operate')}>
                      <LotCreate />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/spray"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurserySprays />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/lots/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('operate')}>
                      <LotDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/locations"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('operate')}>
                      <NurseryLocations />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/tree-types"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('admin')}>
                      <TreeTypeAdmin />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/quarantine"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <QuarantineProtocols />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/propagation/setup"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <PropagationSetup />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/material"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <MaterialList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/material/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <MaterialForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/material/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <MaterialDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/budding"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <BuddingList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/mothers"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <MotherPlants />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stock-items"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('operate')}>
                      <StockItemList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* One stock item: sold to whom and when, where it grew, every count. Sales and the stock seats. */}
              <Route
                path="/nursery/stock-items/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management"
                      alsoModules={[...retailAlso('operate'), { moduleKey: 'sales', licenseType: ['user', 'admin'] }, { moduleKey: 'accounts', licenseType: ['user', 'admin'] }]}>
                      <NurseryStockItemSummary />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* TEMPORARY: the legacy customer import, switched off 2026-10-28. It
                  checks its own seats: a Nursery admin or an Accounts seat. */}
              <Route
                path="/nursery/legacy-import"
                element={
                  <ProtectedRoute>
                    <LegacyCustomerImport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/website-signups"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="sales" moduleName="Sales">
                      <NurseryWebsiteSignupsList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/trade-account-requests"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="sales" moduleName="Sales">
                      <NurseryTradeAccountRequestsList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/delivery-zones"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryDeliveryZonesList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/pickers"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryPickers />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/despatch"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryDespatchPlanner />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/loads"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryLoadList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/loads/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryLoadDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/deliveries"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryDeliveries />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/deliveries/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryDeliveryRun />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/deliveries/:id/stops/:orderId"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryDeliverStop />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/picking"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryPickingLoads />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/picking/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryPickList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stocktake"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('count')}>
                      <NurseryStocktakes />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/colour-codes"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryColourCodes />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/painting"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryPainting />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/forecast"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryForecast />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stocktake/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('count')}>
                      <NurseryStocktakeCount />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* What we have, what is sold and what is at each farm: Sales and the stock seats. */}
              <Route
                path="/nursery/stock-summary"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management"
                      alsoModules={[...retailAlso('operate'), { moduleKey: 'sales', licenseType: ['user', 'admin'] }]}>
                      <NurseryStockSummary />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stock-to-move"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryStockToMove />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stillages"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('operate')}>
                      <NurseryStillages />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/transfers"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryTransferList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/transfers/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryTransferDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/despatchable"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryDespatchPlaces />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route path="/nursery/refills" element={<MovedRoute to="/nursery/despatchable" />} />
              <Route
                path="/nursery/tills"
                element={
                  <ProtectedRoute>
                    <AccountsOrNurseryGate nursery={['manager', 'admin']} retail={['staff', 'manager']} what="The tills">
                      <NurseryTills />
                    </AccountsOrNurseryGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/tills/:id"
                element={
                  <ProtectedRoute>
                    <AccountsOrNurseryGate nursery={['manager', 'admin']} retail={['staff', 'manager']} what="The tills">
                      <NurseryTillDetail />
                    </AccountsOrNurseryGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stock-items/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('admin')}>
                      <StockItemForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stock-items/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('admin')}>
                      <StockItemForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/products"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryProductList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/products/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryProductForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/products/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryProductForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/orders"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" licenseType={['operator', 'manager', 'admin']} alsoModules={ORDER_READERS}>
                      <NurseryOrderList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/orders/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="sales" moduleName="Sales" alsoModules={retailSells()}>
                      <NurseryOrderCreate />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/orders/import"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="sales" moduleName="Sales">
                      <NurseryPoImport />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/cross-dock"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryCrossDockSchedules />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/cross-dock/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryCrossDockScheduleDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/logistics"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryLogisticsHub />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/work"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('admin')}>
                      <NurseryWorkSummary />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/backorders"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryBackorders />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/sales"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurserySalesHub />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stock"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryStockHub />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/retailers"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryRetailerHub />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/retailer-range"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryRetailerRange />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/orders/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" licenseType={['operator', 'manager', 'admin']} alsoModules={ORDER_READERS}>
                      <NurseryOrderDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/orders/:id/draw-down"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('manage')}>
                      <NurseryOrderDrawDown />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/inventory"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryInventoryDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* Accounts: the money side. Customers and their terms, price lists,
                  invoices, receivables, rebates, and the Xero / MYOB connections
                  for invoices and payroll. Gated on the accounts seats; the
                  nursery reads customers and price lists, since an order is
                  taken for a customer at a price. */}
              <Route
                path="/accounts"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <AccountsDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* Sales: the phone system and the call log (docs/sales-module-plan.md). */}
              <Route
                path="/sales"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="sales" moduleName="Sales">
                      <SalesHome />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sales/follow-ups"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="sales" moduleName="Sales" alsoModules={CRM_ALSO}>
                      <SalesFollowUps />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sales/phone"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="sales" moduleName="Sales">
                      <PhoneSettings />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* A retail nursery's shop: its tiles open the shared stock pages. */}
              <Route
                path="/retail"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="retail" moduleName="Retail Nursery">
                      <RetailDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers"
                element={
                  <ProtectedRoute>
                    <AccountsOrNurseryGate nursery={['picker', 'operator', 'manager', 'admin']} retail={['staff', 'manager']} what="The customer list">
                      <NurseryCustomerList />
                    </AccountsOrNurseryGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="sales" moduleName="Sales" alsoModules={[{ moduleKey: 'accounts' }]}>
                      <NurseryCustomerForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="sales" moduleName="Sales" alsoModules={[{ moduleKey: 'accounts' }]}>
                      <NurseryCustomerForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/:id"
                element={
                  <ProtectedRoute>
                    <NurseryCustomerDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/price-lists"
                element={
                  <ProtectedRoute>
                    <AccountsOrNurseryGate nursery={['manager', 'admin']} retail={['manager']} what="The price lists">
                      <NurseryPriceListList />
                    </AccountsOrNurseryGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/price-lists/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts" alsoModules={retailAlso('admin')}>
                      <NurseryPriceListForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/price-lists/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts" alsoModules={retailAlso('admin')}>
                      <NurseryPriceListForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/invoices"
                element={
                  <ProtectedRoute>
                    <AccountsOrNurseryGate nursery={['picker', 'operator', 'manager', 'admin']} retail={['staff', 'manager']} what="The invoices">
                      <NurseryInvoiceList />
                    </AccountsOrNurseryGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/invoices/:id"
                element={
                  <ProtectedRoute>
                    <AccountsOrNurseryGate nursery={['picker', 'operator', 'manager', 'admin']} retail={['staff', 'manager']} what="An invoice">
                      <NurseryInvoiceDetail />
                    </AccountsOrNurseryGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/receivables"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <NurseryReceivables />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/rebates"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <NurseryRebates />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/remittances"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <Remittances />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/suppliers"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts" alsoModules={retailAlso('admin')}>
                      <Suppliers />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* No licence gate: an org admin with no accounts seat approves
                  credit notes, and the page asks the server who may. */}
              <Route
                path="/accounts/credit-note-requests"
                element={
                  <ProtectedRoute>
                    <CreditNoteRequests />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/purchase-order-users"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <PurchaseOrderPeople />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/account-rules"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <AccountRules />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/deposits"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <Deposits />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/legacy-balances"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <LegacyBalances />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/budget"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <Budget />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/bas"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <BasSummary />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/statements"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <MonthlyStatements />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/terms"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <TradingTerms />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/stripe"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <CardPayments />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/xero"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <XeroSettings />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/myob"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <MyobSettings />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/payroll-mappings"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <PayrollExportMappings />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* Where these pages used to live. Bookmarks, emailed links and an
                  app bundle older than the move still arrive on these. */}
              <Route path="/nursery/customers" element={<MovedRoute to="/customers" />} />
              <Route path="/nursery/customers/new" element={<MovedRoute to="/customers/new" />} />
              <Route path="/nursery/customers/:id/edit" element={<MovedRoute to="/customers/:id/edit" />} />
              <Route path="/nursery/customers/:id" element={<MovedRoute to="/customers/:id" />} />
              {/* Customers left /accounts on 2026-10-30: Sales reads and edits them too. */}
              <Route path="/accounts/customers" element={<MovedRoute to="/customers" />} />
              <Route path="/accounts/customers/new" element={<MovedRoute to="/customers/new" />} />
              <Route path="/accounts/customers/:id/edit" element={<MovedRoute to="/customers/:id/edit" />} />
              <Route path="/accounts/customers/:id" element={<MovedRoute to="/customers/:id" />} />
              <Route path="/nursery/price-lists" element={<MovedRoute to="/accounts/price-lists" />} />
              <Route path="/nursery/price-lists/new" element={<MovedRoute to="/accounts/price-lists/new" />} />
              <Route path="/nursery/price-lists/:id/edit" element={<MovedRoute to="/accounts/price-lists/:id/edit" />} />
              <Route path="/nursery/invoices" element={<MovedRoute to="/accounts/invoices" />} />
              <Route path="/nursery/invoices/:id" element={<MovedRoute to="/accounts/invoices/:id" />} />
              <Route path="/nursery/receivables" element={<MovedRoute to="/accounts/receivables" />} />
              <Route path="/nursery/rebates" element={<MovedRoute to="/accounts/rebates" />} />
              <Route path="/nursery/xero" element={<MovedRoute to="/accounts/xero" />} />
              <Route path="/admin/xero-payroll-settings" element={<MovedRoute to="/accounts/xero" />} />
              {/* Payroll and invoicing share one Xero connection, on one page. */}
              <Route path="/accounts/xero-payroll" element={<MovedRoute to="/accounts/xero" />} />
              <Route path="/admin/myob-settings" element={<MovedRoute to="/accounts/myob" />} />
              <Route path="/admin/payroll-mappings" element={<MovedRoute to="/accounts/payroll-mappings" />} />
              {/* Customer orders — auth only, no nursery license needed */}
              <Route
                path="/nursery/my-orders"
                element={
                  <ProtectedRoute>
                    <CustomerOrdersDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Equipment Routes — read/use available to all authenticated users */}
              <Route
                path="/equipment"
                element={
                  <ProtectedRoute>
                    <EquipmentList />
                  </ProtectedRoute>
                }
              />
              {/* Insurance — anyone reads; the forms need a vehicles creator or admin seat */}
              <Route
                path="/equipment/insurance"
                element={
                  <ProtectedRoute>
                    <InsurancePolicies />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/insurance/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <InsurancePolicyForm key="new" mode="new" />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/insurance/:id"
                element={
                  <ProtectedRoute>
                    <InsurancePolicyDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/insurance/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <InsurancePolicyForm key="edit" mode="edit" />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/insurance/:id/renew"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <InsurancePolicyForm key="renew" mode="renew" />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id/pre-start"
                element={
                  <ProtectedRoute>
                    <PreStartFlow />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id/sessions"
                element={
                  <ProtectedRoute>
                    <SessionHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id/services"
                element={
                  <ProtectedRoute>
                    <ServiceRecords />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id"
                element={
                  <ProtectedRoute>
                    <EquipmentDetail />
                  </ProtectedRoute>
                }
              />

              {/* Parts catalogue — any authenticated user can read, write requires license */}
              <Route
                path="/equipment/parts"
                element={
                  <ProtectedRoute>
                    <PartsCatalog />
                  </ProtectedRoute>
                }
              />

              {/* Equipment management — Vehicles module license required */}
              <Route
                path="/equipment/admin"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <EquipmentAdminSettings />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <EquipmentForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <EquipmentForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id/maintenance"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <MaintenanceManagement />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id/attach"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <AttachImplement />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Redirect any unknown routes */}
              <Route
                path="*"
                element={<Navigate to={Capacitor.isNativePlatform() ? '/login' : '/'} replace />}
              />
            </Routes>
            </Suspense>
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
